import { httpsCallable } from 'firebase/functions'

import { functions } from '../../../firebase'

// Type definitions for test queries
export interface TestQuery {
  id?: string
  description: string
  conversationSnippet?: string
  userIntent?: string
  preferences?: Record<string, unknown>
  tags: string[]
  createdAt: Date | string
  updatedAt: Date | string
}

export interface TestQueryInput {
  description: string
  conversationSnippet?: string
  userIntent?: string
  preferences?: Record<string, unknown>
  tags: string[]
}

export interface TestRun {
  id?: string
  name: string
  description?: string
  testQueryIds: string[]
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  startedAt: Date | string
  completedAt?: Date | string
  totalQueries: number
  completedQueries: number
  failedQueries: number
  createdBy?: string
}

// Cloud function response types
interface CreateTestQueryResponse {
  success: boolean
  data?: TestQuery
  error?: string
}

interface GetTestQueriesResponse {
  success: boolean
  data?: {
    queries: TestQuery[]
    totalCount: number
  }
  error?: string
}

interface UpdateTestQueryResponse {
  success: boolean
  data?: TestQuery
  error?: string
}

interface DeleteTestQueryResponse {
  success: boolean
  error?: string
}

interface CreateTestRunResponse {
  success: boolean
  data?: TestRun
  error?: string
}

interface GetTestRunsResponse {
  success: boolean
  data?: {
    testRuns: TestRun[]
    totalCount: number
  }
  error?: string
}

export class TestQueryService {
  /**
   * Create a new test query
   */
  static async createTestQuery(input: TestQueryInput): Promise<TestQuery> {
    try {
      const createTestQueryFunction = httpsCallable<
        TestQueryInput,
        CreateTestQueryResponse
      >(functions, 'createTestQuery')

      const result = await createTestQueryFunction(input)

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to create test query')
      }

      if (!result.data.data) {
        throw new Error('No test query data returned')
      }

      return result.data.data
    } catch (error) {
      console.error('Error creating test query:', error)
      throw new Error(
        `Failed to create test query: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Get test queries with optional filtering and pagination
   */
  static async getTestQueries(options?: {
    limit?: number
    offset?: number
    tags?: string[]
  }): Promise<{ queries: TestQuery[]; totalCount: number }> {
    try {
      const getTestQueriesFunction = httpsCallable<
        { limit?: number; offset?: number; tags?: string[] },
        GetTestQueriesResponse
      >(functions, 'getTestQueries')

      const result = await getTestQueriesFunction(options || {})

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to get test queries')
      }

      if (!result.data.data) {
        throw new Error('No test queries data returned')
      }

      return result.data.data
    } catch (error) {
      console.error('Error getting test queries:', error)
      throw new Error(
        `Failed to get test queries: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Update an existing test query
   */
  static async updateTestQuery(
    id: string,
    input: Partial<TestQueryInput>
  ): Promise<TestQuery> {
    try {
      const updateTestQueryFunction = httpsCallable<
        TestQueryInput & { id: string },
        UpdateTestQueryResponse
      >(functions, 'updateTestQuery')

      const result = await updateTestQueryFunction({
        id,
        ...input,
      } as TestQueryInput & { id: string })

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to update test query')
      }

      if (!result.data.data) {
        throw new Error('No test query data returned')
      }

      return result.data.data
    } catch (error) {
      console.error('Error updating test query:', error)
      throw new Error(
        `Failed to update test query: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Delete a test query
   */
  static async deleteTestQuery(id: string): Promise<void> {
    try {
      const deleteTestQueryFunction = httpsCallable<
        { id: string },
        DeleteTestQueryResponse
      >(functions, 'deleteTestQuery')

      const result = await deleteTestQueryFunction({ id })

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to delete test query')
      }
    } catch (error) {
      console.error('Error deleting test query:', error)
      throw new Error(
        `Failed to delete test query: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Create and run a test run
   */
  static async createTestRun(input: {
    name: string
    description?: string
    testQueryIds: string[]
  }): Promise<TestRun> {
    try {
      const createTestRunFunction = httpsCallable<
        { name: string; description?: string; testQueryIds: string[] },
        CreateTestRunResponse
      >(functions, 'createTestRun')

      const result = await createTestRunFunction(input)

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to create test run')
      }

      if (!result.data.data) {
        throw new Error('No test run data returned')
      }

      return result.data.data
    } catch (error) {
      console.error('Error creating test run:', error)
      throw new Error(
        `Failed to create test run: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  /**
   * Get test runs with optional filtering and pagination
   */
  static async getTestRuns(options?: {
    limit?: number
    offset?: number
    status?: string
  }): Promise<{ testRuns: TestRun[]; totalCount: number }> {
    try {
      const getTestRunsFunction = httpsCallable<
        { limit?: number; offset?: number; status?: string },
        GetTestRunsResponse
      >(functions, 'getTestRuns')

      const result = await getTestRunsFunction(options || {})

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to get test runs')
      }

      if (!result.data.data) {
        throw new Error('No test runs data returned')
      }

      return result.data.data
    } catch (error) {
      console.error('Error getting test runs:', error)
      throw new Error(
        `Failed to get test runs: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }
}
